import {FlowStatePresentation} from "@nolimitcity/slot-game/bin/flowcontrol/FlowStatePresentation";
import {FlowStateController} from "@nolimitcity/slot-game/bin/flowcontrol/FlowStateController";
import {IBetWayWinData, IFinalEvaluatedArea, IMentalGameData} from "../server/MentalData";
import {Mental} from "../Mental";
import {MentalGameMode} from "../server/MentalGameMode";
import {TimelineLite, TimelineMax, TweenLite} from "gsap";
import {XFeaturePresentation} from "./xFeature/XFeaturePresentation";
import {ReelEnhancer} from "../server/ReelEnhancerData";
import {Logger} from "@nolimitcity/slot-launcher/bin/utils/Logger";
import {MentalNormalSymbol} from "../reel/symbols/MentalNormalSymbol";
import {MentalSymbolStates} from "../reel/MentalSymbol";
import {FeaturePresentationHelper} from "../utils/FeaturePresentationHelper";
import {ReelModelState, ReelModelSymbolData} from "../../framework/reel/ReelModel";
import {MentalReel} from "../reel/MentalReel";
import {SoundManager} from "../sound/SoundManager";
import {MentalSoundConfig} from "../MentalSoundConfig";
import {MentalHelper} from "../utils/MentalHelper";
import {MentalScatter2Explosion} from "../features/MentalScatter2Explosion";
import {NoWinPresentation} from "../noWin/NoWinPresentation";
import {MentalFeatureIndicatorView} from "../features/MentalFeatureIndicatorView";
import {EnhancerReelPresentation} from "./enhancerReel/EnhancerReelPresentation";
import {MentalBigWinAnticipationView} from "../features/MentalBigWinAnticipationView";

enum winPresentationState {
    STICKY_SCATTER = "STICKY_SCATTER",

    // #278
    ALL_S2_AT_ONCE_FEATURE_BUY = "ALL_S2_AT_ONCE_FEATURE_BUY",

    // enhancer
    ENHANCER_REPLACE_X_NUDGE = "ENHANCER_REPLACE_X_NUDGE",
    ENHANCER_OPEN = "ENHANCER_OPEN",
    ENHANCER_X_NUDGE_REEL_2 = "ENHANCER_X_NUDGE_REEL_2",
    ENHANCER_X_NUDGE_REEL_4 = "ENHANCER_X_NUDGE_REEL_4",
    ENHANCER_X_NUDGE_WIN = "ENHANCER_X_NUDGE_WIN",
    ENHANCER_X_SPLIT = "ENHANCER_X_SPLIT",
    ENHANCER_X_WAYS_REEL_4 = "ENHANCER_X_WAYS_REEL_4",

    HIDE_ALL_X_WAYS = "HIDE_ALL_X_WAYS",

    // normal x-split
    NORMAL_X_SPLIT_REEL_2_0 = "NORMAL_X_SPLIT_REEL_2_0",
    NORMAL_X_SPLIT_REEL_2_1 = "NORMAL_X_SPLIT_REEL_2_1",
    NORMAL_X_SPLIT_REEL_4_0 = "NORMAL_X_SPLIT_REEL_4_0",
    NORMAL_X_SPLIT_REEL_4_1 = "NORMAL_X_SPLIT_REEL_4_1",
    NORMAL_X_SPLIT_REEL_4_2 = "NORMAL_X_SPLIT_REEL_4_2",
    NORMAL_X_SPLIT_REEL_4_3 = "NORMAL_X_SPLIT_REEL_4_3",

    // normal x-ways
    NORMAL_X_WAYS_REEL_2_0 = "NORMAL_X_WAYS_REEL_2_0",
    NORMAL_X_WAYS_REEL_2_1 = "NORMAL_X_WAYS_REEL_2_1",
    NORMAL_X_WAYS_REEL_4_0 = "NORMAL_X_WAYS_REEL_4_0",
    NORMAL_X_WAYS_REEL_4_1 = "NORMAL_X_WAYS_REEL_4_1",
    NORMAL_X_WAYS_REEL_4_2 = "NORMAL_X_WAYS_REEL_4_2",
    NORMAL_X_WAYS_REEL_4_3 = "NORMAL_X_WAYS_REEL_4_3",

    // s2 symbol transform
    S2_SYM_TRANSFORM_2_0 = "S2_SYM_TRANSFORM_2_0",
    S2_SYM_TRANSFORM_2_1 = "S2_SYM_TRANSFORM_2_1",
    S2_SYM_TRANSFORM_4_0 = "S2_SYM_TRANSFORM_4_0",
    S2_SYM_TRANSFORM_4_1 = "S2_SYM_TRANSFORM_4_1",
    S2_SYM_TRANSFORM_4_2 = "S2_SYM_TRANSFORM_4_2",
    S2_SYM_TRANSFORM_4_3 = "S2_SYM_TRANSFORM_4_3",

    // dead feature
    DEAD_FEATURE = "DEAD_FEATURE",

    // update xWays final Counter
    UPDATE_FINAL_X_WAYS_COUNTER = "UPDATE_FINAL_X_WAYS_COUNTER",

    // dim down no win symbols
    DIM_NO_WIN = "DIM_NO_WIN",

    // initial-WIN
    INITIAL_WIN = "INITIAL_WIN",

    // Big win anticipation
    BIG_WIN_ANTICIPATION = "BIG_WIN_ANTICIPATION",

    // Big-Win
    BIG_WIN_INIT = "BIG_WIN_INIT",
    LEVEL_1 = "LEVEL_1",
    LEVEL_2 = "LEVEL_2",
    LEVEL_3 = "LEVEL_3",
    LEVEL_4 = "LEVEL_4",
    LEVEL_5 = "LEVEL_5",
    BIG_WIN_END = "BIG_WIN_END",
}

interface IS2ExplodeIdxWithXWaysOccurred {
    explodeIdx: number,
    xWaysOccurred: boolean
}

interface IXSplitKnifePosWithXWays {
    yPos: number,
    xWaysOccurred: boolean,
    xWaysBgHeight: number
}

export class MentalWinPresentation implements FlowStatePresentation {
    private _gameData: IMentalGameData;
    private presentationTl: TimelineMax;
    private useTotalWin: boolean;
    private _winSequenceArray: winPresentationState[] = [];
    private _controller: FlowStateController;

    private _bigWinSoundName: string = "";

    private static playScatter2ExplodeInXSplitAction(baseSymIdForSplit: number, positions: IXSplitKnifePosWithXWays[]): void {
        const numReels: number = 5;

        let xWaysOccurred: boolean,
            yPos: number,
            xWaysBgHeight: number;

        for (let i: number = 0; i < numReels; i++) {
            yPos = positions[i].yPos;
            xWaysOccurred = positions[i].xWaysOccurred;
            xWaysBgHeight = positions[i].xWaysBgHeight;

            // when we are passing the yPos, splitIdx sent in has no significance.
            MentalScatter2Explosion.playScatter2ExplosionAnimation(i, baseSymIdForSplit, 0, xWaysOccurred, yPos, xWaysBgHeight);
        }
    }

    private static playKnifeAnimation(positions: IXSplitKnifePosWithXWays[]): void {
        const numReels: number = 5;

        for (let i: number = 0; i < numReels; i++) {
            Mental.xSplitAnimations.playKnifeAnimation(i, positions[i].yPos);
        }
    }

    public start(flowStateController: FlowStateController): void {
        this._gameData = Mental.model.gameData;
        this.useTotalWin = this._gameData.brokeTheBank || this._gameData.accumulatedRoundWin != this._gameData.totalBetWayWinnings || Mental.model.data.nextMode != MentalGameMode.NORMAL;
        this.presentationTl = new TimelineMax();
        this._controller = flowStateController;

        this.createWinPresentationSequence();
        this.playPresentations();

        this._gameData.mode !== MentalGameMode.NORMAL && this.presentationTl.add(TweenLite.to(this, {duration: 1})); // extra offset time to make presentation smooth
    }

    private createWinPresentationSequence(): void {
        const e2Data: IFinalEvaluatedArea = this._gameData.finalEvaluatedArea[3][2][0];

        this._winSequenceArray = [];

        // sticky scatter
        if (this._gameData.nextMode.indexOf("FREESPIN") > -1) {
            this._winSequenceArray.push(winPresentationState.STICKY_SCATTER);
        }

        // #278 => feature buy + main game + no win
        if (this._gameData.wasFeatureBuy && this._gameData.mode === MentalGameMode.NORMAL && this._gameData.betWayWins.length === 0) {
            this._winSequenceArray.push(winPresentationState.ALL_S2_AT_ONCE_FEATURE_BUY);
        } else {
            this.createSequenceForS2(1);
            this.createSequenceForS2(3);
        }

        // replace enhancer symbol with xNudge before showing enhancer reel.
        if (Mental.xNudgePresentation.xNudgeInEnhancer()) {
            this._winSequenceArray.push(winPresentationState.ENHANCER_REPLACE_X_NUDGE);
        }
        // enhancer open, plus if it has mid? dead? wild? & xNudge its already being placed at enhancer position
        if (this._gameData.specialReelsOpen.indexOf(true) > -1) {
            this._winSequenceArray.push(winPresentationState.ENHANCER_OPEN);
        }
        // E2 xWays
        if (this._gameData.specialReelsSymbols[1] === ReelEnhancer.XWAYS) {
            this._winSequenceArray.push(winPresentationState.ENHANCER_X_WAYS_REEL_4);
        }
        // (E1 xSplit and E2 xWays) OR E2 xSplit
        if ((this._gameData.specialReelsSymbols[0] === ReelEnhancer.XSPLIT && (this._gameData.specialReelsSymbols[1] === ReelEnhancer.XWAYS || (!e2Data.wasDead && e2Data.multiplier > 999))) || (this._gameData.specialReelsSymbols[1] === ReelEnhancer.XSPLIT)) {
            this._winSequenceArray.push(winPresentationState.ENHANCER_X_SPLIT);
        }
        // hide all x-ways
        if (this._gameData.firstS2FeatureTriggered.includes(ReelEnhancer.XWAYS)
            || this._gameData.specialReelsSymbols.includes(ReelEnhancer.XWAYS)
            || this._gameData.secondS2FeatureTriggered.includes(ReelEnhancer.XWAYS)) {
            this._winSequenceArray.push(winPresentationState.HIDE_ALL_X_WAYS);
        }
        // #316 => E1 xNudge and xWays in the game, wait for xWays to finish first.
        if ((this._gameData.specialReelsSymbols[0] === ReelEnhancer.XNUDGE_WILDS) && (this._gameData.firstS2FeatureTriggered.includes(ReelEnhancer.XWAYS) || this._gameData.secondS2FeatureTriggered.includes(ReelEnhancer.XWAYS) || this._gameData.specialReelsSymbols[1] === ReelEnhancer.XWAYS || (!e2Data.wasDead && e2Data.multiplier > 999))) {
            this._winSequenceArray.push(winPresentationState.ENHANCER_X_NUDGE_REEL_2);
        }
        // E2 xNudge
        if (this._gameData.specialReelsSymbols[1] === ReelEnhancer.XNUDGE_WILDS) {
            this._winSequenceArray.push(winPresentationState.ENHANCER_X_NUDGE_REEL_4);
        }

        // dead-feature-presentation
        if (Mental.deadFeaturePresentation.checkForPresentation()) {
            this._winSequenceArray.push(winPresentationState.DEAD_FEATURE);
        }

        if (Mental.xNudgePresentation.xNudgeInEnhancer() && this._gameData.betWayWins.length) {
            this._winSequenceArray.push(winPresentationState.ENHANCER_X_NUDGE_WIN);
        }

        this._winSequenceArray.push(winPresentationState.UPDATE_FINAL_X_WAYS_COUNTER);

        if (NoWinPresentation.isWinRound()) {
            this._winSequenceArray.push(winPresentationState.DIM_NO_WIN);
        }

        // bigWin stuff
        if (Mental.bigWinPresentation.isBigWinTriggered()) {
            // #366
            if (Mental.isBigWinAnticipation) {
                this._winSequenceArray.push(winPresentationState.BIG_WIN_ANTICIPATION);
            }

            // #252
            this._winSequenceArray.push(winPresentationState.BIG_WIN_INIT);

            const levels: winPresentationState[] = [
                winPresentationState.LEVEL_1,
                winPresentationState.LEVEL_2,
                winPresentationState.LEVEL_3,
                winPresentationState.LEVEL_4,
                winPresentationState.LEVEL_5
            ];
            for (let i = 0; i < Number(this._gameData.currentWinRatioConfig.text?.split("_")[1]); i++) {
                this._winSequenceArray.push(levels[i]);
            }
            this._winSequenceArray.push(winPresentationState.BIG_WIN_END);
        }
        // initial-win-presentation if no bigWin
        else if (Mental.initialWinPresentation.checkForPresentation()) {
            this._winSequenceArray.push(winPresentationState.INITIAL_WIN);
        }

        Logger.logDev("current Win Sequence:", this._winSequenceArray);
    }

    private createSequenceForS2(reel: number): void {
        const possibleSymbols: string[] = ["M1", "M2", "M3", "M4", "M5", "W", "D"];
        let featureToCheck: string;

        if (reel === 1) {
            featureToCheck = this._gameData.firstS2FeatureTriggered[0];
            if (possibleSymbols.includes(featureToCheck)) {
                this._winSequenceArray.push(winPresentationState.S2_SYM_TRANSFORM_2_0);
            } else if (featureToCheck === ReelEnhancer.XSPLIT) {
                this._winSequenceArray.push(winPresentationState.NORMAL_X_SPLIT_REEL_2_0);
            } else if (featureToCheck === ReelEnhancer.XWAYS) {
                this._winSequenceArray.push(winPresentationState.NORMAL_X_WAYS_REEL_2_0);
            }

            featureToCheck = this._gameData.firstS2FeatureTriggered[1];
            if (possibleSymbols.includes(featureToCheck)) {
                this._winSequenceArray.push(winPresentationState.S2_SYM_TRANSFORM_2_1);
            } else if (featureToCheck === ReelEnhancer.XSPLIT) {
                this._winSequenceArray.push(winPresentationState.NORMAL_X_SPLIT_REEL_2_1);
            } else if (featureToCheck === ReelEnhancer.XWAYS) {
                this._winSequenceArray.push(winPresentationState.NORMAL_X_WAYS_REEL_2_1);
            }
        } else {
            featureToCheck = this._gameData.secondS2FeatureTriggered[0];
            if (possibleSymbols.includes(featureToCheck)) {
                this._winSequenceArray.push(winPresentationState.S2_SYM_TRANSFORM_4_0);
            } else if (featureToCheck === ReelEnhancer.XSPLIT) {
                this._winSequenceArray.push(winPresentationState.NORMAL_X_SPLIT_REEL_4_0);
            } else if (featureToCheck === ReelEnhancer.XWAYS) {
                this._winSequenceArray.push(winPresentationState.NORMAL_X_WAYS_REEL_4_0);
            }

            featureToCheck = this._gameData.secondS2FeatureTriggered[1];
            if (possibleSymbols.includes(featureToCheck)) {
                this._winSequenceArray.push(winPresentationState.S2_SYM_TRANSFORM_4_1);
            } else if (featureToCheck === ReelEnhancer.XSPLIT) {
                this._winSequenceArray.push(winPresentationState.NORMAL_X_SPLIT_REEL_4_1);
            } else if (featureToCheck === ReelEnhancer.XWAYS) {
                this._winSequenceArray.push(winPresentationState.NORMAL_X_WAYS_REEL_4_1);
            }

            featureToCheck = this._gameData.secondS2FeatureTriggered[2];
            if (possibleSymbols.includes(featureToCheck)) {
                this._winSequenceArray.push(winPresentationState.S2_SYM_TRANSFORM_4_2);
            } else if (featureToCheck === ReelEnhancer.XSPLIT) {
                this._winSequenceArray.push(winPresentationState.NORMAL_X_SPLIT_REEL_4_2);
            } else if (featureToCheck === ReelEnhancer.XWAYS) {
                this._winSequenceArray.push(winPresentationState.NORMAL_X_WAYS_REEL_4_2);
            }

            featureToCheck = this._gameData.secondS2FeatureTriggered[3];
            if (possibleSymbols.includes(featureToCheck)) {
                this._winSequenceArray.push(winPresentationState.S2_SYM_TRANSFORM_4_3);
            } else if (featureToCheck === ReelEnhancer.XSPLIT) {
                this._winSequenceArray.push(winPresentationState.NORMAL_X_SPLIT_REEL_4_3);
            } else if (featureToCheck === ReelEnhancer.XWAYS) {
                this._winSequenceArray.push(winPresentationState.NORMAL_X_WAYS_REEL_4_3);
            }
        }
    }

    /**
     * function to show enhancer reel on all reelStop.
     * @private
     */
    private showEnhancerReel(): TimelineLite {
        const tl: TimelineLite = new TimelineLite(),
            gameData: IMentalGameData = this._gameData,
            firstEnhancerFeature: string = gameData.specialReelsSymbols[0],
            secondEnhancerFeature: string = gameData.specialReelsSymbols[1],
            // #316
            xWaysAnyS2OrE2: boolean = (gameData.firstS2FeatureTriggered.includes(ReelEnhancer.XWAYS) || gameData.secondS2FeatureTriggered.includes(ReelEnhancer.XWAYS) || secondEnhancerFeature === ReelEnhancer.XWAYS);

        tl.add(() => this.enhancerOpenAction(firstEnhancerFeature, secondEnhancerFeature));

        // do not throw multi if xWays And xSplit is on enhancer
        // putting this code here to fix #353
        if (firstEnhancerFeature !== ReelEnhancer.XWAYS && firstEnhancerFeature !== ReelEnhancer.XSPLIT) {
            tl.add(Mental.deadFeatureStickyMultiplier.playGiveOutAnim(this._gameData.stickyDeadSymCounter, 1, 2, 0), 0.29);
        }
        // do not throw multi if xWays And xSplit is on enhancer
        // putting this code here to fix #353
        if (secondEnhancerFeature !== ReelEnhancer.XWAYS && secondEnhancerFeature !== ReelEnhancer.XSPLIT) {
            tl.add(Mental.deadFeatureStickyMultiplier.playGiveOutAnim(this._gameData.stickyDeadSymCounter, 3, 2, 0), 0.29);
        }
        tl.add(() => {
            for (let i: number = 0; i < 2; i++) {
                const reelId: number = ((i === 0) ? 1 : 3);
                // M, W or D on enhancers => keep D also, to create black dead with state 99.
                if ((gameData.specialReelsSymbols[i].indexOf("M") > -1) || (gameData.specialReelsSymbols[i] === "W") || (gameData.specialReelsSymbols[i] === "D")) {
                    const sym: MentalNormalSymbol = Mental.reelArea.getReel(reelId).model.getReelSymbolAt(2).positionable as MentalNormalSymbol;
                    sym.setState(MentalSymbolStates.NINETY_NINE, 99);
                    gameData.currentSymbolSize[reelId][2] = 1; // currentSymbolSize updated
                    Mental.xWaysCounter.updateXwaysCounterFromEnhancerOpen(reelId);
                }
                // xNudge wild on enhancers
                else if (gameData.specialReelsSymbols[i] === ReelEnhancer.XNUDGE_WILDS) {
                    Mental.enhancerFrame.hideEnhancerBorder(i);

                    // #92
                    if (firstEnhancerFeature === ReelEnhancer.XNUDGE_WILDS && !xWaysAnyS2OrE2) {
                        MentalFeatureIndicatorView.playXNudgeActiveIndicator();
                    }
                }
            }
            // #52, xSplit on any of the enhancer
            const xSplitIdx: number = gameData.specialReelsSymbols.indexOf(ReelEnhancer.XSPLIT);
            if (xSplitIdx > -1) {
                const reelId: number = ((xSplitIdx === 0) ? 1 : 3);
                Mental.xWaysCounter.updateXwaysCounterFromEnhancerOpen(reelId);
                Mental.xSplitAnimations.playXSplitLogoAnimationEnhancer(reelId);
            }
        }, 0.29);

        // #269, if D are eligible for DG then it's time should have been added in order to stop #269, since all the work in upgrade to DG is in timeline.
        if (firstEnhancerFeature === "D") {
            tl.add(Mental.deadFeaturePresentation.tryDeadToGoldOnEnhancerCell(1), 0.29);
        }
        // #269, same as above
        if (secondEnhancerFeature === "D") {
            tl.add(Mental.deadFeaturePresentation.tryDeadToGoldOnEnhancerCell(3), 0.29);
        }

        // #291 => xWays
        if (gameData.specialReelsSymbols.includes(ReelEnhancer.XWAYS)) {
            // #92
            if (firstEnhancerFeature === ReelEnhancer.XWAYS) {
                tl.add(() => MentalFeatureIndicatorView.playXWaysActiveIndicator(), 0.29);
            }
            tl.add(Mental.enhancerReelPresentation.showXWaysLogoBg(gameData), 0.29);
        }

        // #251
        this.handleSpecialActionInE1(tl, firstEnhancerFeature, secondEnhancerFeature);

        return tl;
    }

    private enhancerOpenAction(firstEnhancerFeature: string, secondEnhancerFeature: string): void {
        const isE1Open: boolean = this._gameData.specialReelsOpen[0],
            isE2Open: boolean = this._gameData.specialReelsOpen[1];

        Mental.fireFrame.playBoxOpenAnim(isE1Open, isE2Open);

        Mental.reelArea.reelAreaMask([isE1Open, isE2Open]);
        Mental.fireFrame.hideEnhancerMeter(isE1Open, isE2Open);

        if (isE1Open) {
            MentalScatter2Explosion.playScatter2ExplosionAnimation(1, 2, 0, false, 634.5, 99);

            // #195
            if (firstEnhancerFeature === ReelEnhancer.XSPLIT) {
                SoundManager.playSoundEffect(MentalSoundConfig.EXPLOSION_BLOOD);
            }
            // #203
            else if (firstEnhancerFeature === ReelEnhancer.XWAYS || firstEnhancerFeature === ReelEnhancer.XNUDGE_WILDS) {
                SoundManager.playSoundEffect(MentalSoundConfig.EXPLOSION_ELECTRICITY);
            } else {
                SoundManager.playSoundEffect(MentalSoundConfig.EXPLOSION[(MentalHelper.getRandomIntInRange(0, 2))]);
            }
        }
        if (isE2Open) {
            MentalScatter2Explosion.playScatter2ExplosionAnimation(3, 2, 0, false, 634.5, 99);

            // #195, #370
            if ((firstEnhancerFeature !== secondEnhancerFeature) && (secondEnhancerFeature === ReelEnhancer.XSPLIT)) {
                SoundManager.playSoundEffect(MentalSoundConfig.EXPLOSION_BLOOD);
            }
            // #203, #370
            else if ((firstEnhancerFeature !== secondEnhancerFeature) && (secondEnhancerFeature === ReelEnhancer.XWAYS || secondEnhancerFeature === ReelEnhancer.XNUDGE_WILDS)) {
                SoundManager.playSoundEffect(MentalSoundConfig.EXPLOSION_ELECTRICITY);
            } else {
                // #370
                const specialActionInFirstEnhancer: boolean = (firstEnhancerFeature === ReelEnhancer.XSPLIT || firstEnhancerFeature === ReelEnhancer.XWAYS || firstEnhancerFeature === ReelEnhancer.XNUDGE_WILDS);
                if ((specialActionInFirstEnhancer) && (firstEnhancerFeature !== secondEnhancerFeature)) {
                    SoundManager.playSoundEffect(MentalSoundConfig.EXPLOSION[(MentalHelper.getRandomIntInRange(0, 2))]);
                }
            }
        }
    }

    // #251
    private handleSpecialActionInE1(tl: TimelineLite, firstEnhancerFeature: string, secondEnhancerFeature: string): void {
        const specialActionInFirstEnhancer: boolean = (firstEnhancerFeature === ReelEnhancer.XSPLIT || firstEnhancerFeature === ReelEnhancer.XWAYS || firstEnhancerFeature === ReelEnhancer.XNUDGE_WILDS),
            // #316
            xWaysAnyS2OrE2: boolean = (this._gameData.firstS2FeatureTriggered.includes(ReelEnhancer.XWAYS) || this._gameData.secondS2FeatureTriggered.includes(ReelEnhancer.XWAYS) || secondEnhancerFeature === ReelEnhancer.XWAYS),
            e2Data: IFinalEvaluatedArea = this._gameData.finalEvaluatedArea[3][2][0];

        // xWays has already been taken care at line no. 323, still we count it here, so that it doesn't go in else part.
        if (specialActionInFirstEnhancer) {
            if (firstEnhancerFeature === ReelEnhancer.XSPLIT) {
                // if xWays on E2, we have to play xSplit later, playing this case from regular win presentation sequence
                if (secondEnhancerFeature === ReelEnhancer.XWAYS || (!e2Data.wasDead && e2Data.multiplier > 999)) {
                    tl.to(this, {duration: 0.1}); // 0.1 so that xSplit logo gets some time added
                }
                // no need to check xNudge, since xSplit and xNudge both can't be in Enhancers simultaneously.
                else {
                    tl.add(Mental.enhancerReelPresentation.playEnhancerXSplitPresentation(), 0.4); // 0.4 so that xSplit logo is played before
                }
            } else if (firstEnhancerFeature === ReelEnhancer.XNUDGE_WILDS) {
                if (!e2Data.wasDead && e2Data.multiplier > 999) {
                    tl.to(this, {duration: 0.1});
                }
                else if (!xWaysAnyS2OrE2) {
                    tl.add(Mental.xNudgePresentation.playXNudgePresentation(0), 0.4);
                } else {
                    tl.to(this, {duration: 0.1});
                }
            }
        }
        // it means M/W/D on E1
        else {
            if (secondEnhancerFeature === ReelEnhancer.XWAYS || secondEnhancerFeature === ReelEnhancer.XSPLIT) {
                tl.to(this, {duration: 0.1}); // give it some time, so that xWays/xSplit logo plays and add some time
            }
        }
    }

    private playNext(): void {
        SoundManager.reSetPrevMultSound();
        this._winSequenceArray.shift();
        this.playPresentations();
    }

    /**
     * function to play all the win presentation from here
     * @private
     */
    private playPresentations(): void {
        if (this._winSequenceArray.length) {
            this.resetTl();
            switch (this._winSequenceArray[0]) {
                case winPresentationState.STICKY_SCATTER:
                    this.presentationTl.to({}, {duration: 0.2});
                    this.stickyScatter();
                    break;

                // #278
                case winPresentationState.ALL_S2_AT_ONCE_FEATURE_BUY:
                    this.presentationTl.add(this.handleAllS2AtOnceFeatureBuy());
                    break;

                case winPresentationState.ENHANCER_REPLACE_X_NUDGE:
                case winPresentationState.ENHANCER_OPEN:
                case winPresentationState.ENHANCER_X_SPLIT:
                case winPresentationState.ENHANCER_X_NUDGE_REEL_2:
                case winPresentationState.ENHANCER_X_NUDGE_REEL_4:
                case winPresentationState.ENHANCER_X_NUDGE_WIN:
                case winPresentationState.ENHANCER_X_WAYS_REEL_4:
                    this.enhancerPresentationCases(this._winSequenceArray[0]);
                    break;

                case winPresentationState.NORMAL_X_WAYS_REEL_2_0:
                case winPresentationState.NORMAL_X_WAYS_REEL_2_1:
                case winPresentationState.NORMAL_X_WAYS_REEL_4_0:
                case winPresentationState.NORMAL_X_WAYS_REEL_4_1:
                case winPresentationState.NORMAL_X_WAYS_REEL_4_2:
                case winPresentationState.NORMAL_X_WAYS_REEL_4_3:
                case winPresentationState.HIDE_ALL_X_WAYS:
                    this.scatterTwoXWaysCase(this._winSequenceArray[0]);
                    break;

                case winPresentationState.NORMAL_X_SPLIT_REEL_2_0:
                case winPresentationState.NORMAL_X_SPLIT_REEL_2_1:
                case winPresentationState.NORMAL_X_SPLIT_REEL_4_0:
                case winPresentationState.NORMAL_X_SPLIT_REEL_4_1:
                case winPresentationState.NORMAL_X_SPLIT_REEL_4_2:
                case winPresentationState.NORMAL_X_SPLIT_REEL_4_3:
                    this.scatterTwoXSplitCases(this._winSequenceArray[0]);
                    break;

                case winPresentationState.S2_SYM_TRANSFORM_2_0:
                case winPresentationState.S2_SYM_TRANSFORM_2_1:
                case winPresentationState.S2_SYM_TRANSFORM_4_0:
                case winPresentationState.S2_SYM_TRANSFORM_4_1:
                case winPresentationState.S2_SYM_TRANSFORM_4_2:
                case winPresentationState.S2_SYM_TRANSFORM_4_3:
                    this.scatterTwoTransformCase(this._winSequenceArray[0]);
                    break;

                case winPresentationState.DEAD_FEATURE:
                    this.presentationTl.add(Mental.deadFeaturePresentation.playPresentation());
                    break;

                case winPresentationState.UPDATE_FINAL_X_WAYS_COUNTER:
                    this.presentationTl.add(Mental.xWaysCounter.updateFinalXwaysCounter());
                    break;

                case winPresentationState.DIM_NO_WIN:
                    this.presentationTl.add(NoWinPresentation.dimAtInitialWin());
                    break;

                case winPresentationState.BIG_WIN_ANTICIPATION:
                case winPresentationState.BIG_WIN_INIT:
                case winPresentationState.LEVEL_1:
                case winPresentationState.LEVEL_2:
                case winPresentationState.LEVEL_3:
                case winPresentationState.LEVEL_4:
                case winPresentationState.LEVEL_5:
                case winPresentationState.BIG_WIN_END:
                case winPresentationState.INITIAL_WIN:
                    // #195
                    this._bigWinSoundName = "";
                    this.bigWinAndInitialWinCases(this._winSequenceArray[0]);
                    break;
            }
            this.presentationTl.add(() => this.playNext(), this.presentationTl.totalDuration() + 0.01);
        } else {
            this.presentationTl.add(() => this.showWinField());
            this.endPresentation();
        }
    }

    private resetTl(): void {
        this.presentationTl = MentalHelper.timeLineKiller(this.presentationTl, new TimelineMax());
        // #365
        this._controller.setSkipCallback(undefined);
        if (this._winSequenceArray[0] !== winPresentationState.BIG_WIN_INIT) {
            this.presentationTl.add(() => {
                this._controller.setSkipCallback(() => this.skip());
            }, 0.1);
        }
    }

    private bigWinAndInitialWinCases(condition: string): void {
        switch (condition) {
            case winPresentationState.BIG_WIN_ANTICIPATION:
                // #366
                this.presentationTl.add(() => MentalBigWinAnticipationView.playBigWinAnticipation(), 0);

                this.presentationTl.add(() => MentalBigWinAnticipationView.stopBigWinAnticipation(), 1);

                break;
            case winPresentationState.BIG_WIN_INIT:
                // #195
                this.presentationTl.add(() => SoundManager.playBigWinInit(), 0);

                this.presentationTl.add(this.playAllWinSymbols(), 0);

                this.presentationTl.add([
                    this.playAllWinSymbolsBigWinInit(),
                    Mental.winWays.showInitialWinWays() // #417
                ], 0.8);

                break;
            case winPresentationState.LEVEL_1:
                // #195
                this.presentationTl.add(() => {
                    SoundManager.fadeSound(MentalSoundConfig.INIT_WIN_BIG_WIN, 1, 0, 0.1);

                    this.playBigWinLevelSound(MentalSoundConfig.BIG_WIN[0]);
                }, 0);

                this.presentationTl.add(Mental.bigWinPresentation.playBigWinLevelAnimation(1), 0);

                break;
            case winPresentationState.LEVEL_2:
                // #195
                this.presentationTl.add(() => this.playBigWinLevelSound(MentalSoundConfig.BIG_WIN[1]), 0);

                this.presentationTl.add(Mental.bigWinPresentation.playBigWinLevelAnimation(2), 0);

                break;
            case winPresentationState.LEVEL_3:
                // #195
                this.presentationTl.add(() => this.playBigWinLevelSound(MentalSoundConfig.BIG_WIN[2]), 0);

                this.presentationTl.add(Mental.bigWinPresentation.playBigWinLevelAnimation(3), 0);

                break;
            case winPresentationState.LEVEL_4:
                // #195
                this.presentationTl.add(() => this.playBigWinLevelSound(MentalSoundConfig.BIG_WIN[3]), 0);

                this.presentationTl.add(Mental.bigWinPresentation.playBigWinLevelAnimation(4), 0);

                break;
            case winPresentationState.LEVEL_5:
                // #195
                this.presentationTl.add(() => this.playBigWinLevelSound(MentalSoundConfig.BIG_WIN[4]), 0);

                this.presentationTl.add(Mental.bigWinPresentation.playBigWinLevelAnimation(5));
                break;
            case winPresentationState.BIG_WIN_END:
                this.presentationTl.add(() => SoundManager.playBigWinEnd(), 0);

                this.presentationTl.add(Mental.bigWinPresentation.playEndingAnimation(), 0);
                break;
            case winPresentationState.INITIAL_WIN:
                // #146
                if (this._gameData.currentWinRatioConfig.duration > 0.001) {
                    this.presentationTl.add(() => SoundManager.playSoundEffect(MentalSoundConfig.INIT_WIN, 0, 1));
                    this.presentationTl.add(this.playAllWinSymbols());
                    this.presentationTl.add(Mental.initialWinPresentation.startPresentation());
                } else {
                    this.presentationTl.add([
                        this.playAllWinSymbols(),
                        Mental.initialWinPresentation.startPresentation()
                    ]);
                }
                break;
        }
    }

    private enhancerPresentationCases(condition: string): void {
        switch (condition) {
            case winPresentationState.ENHANCER_REPLACE_X_NUDGE: // replace enhancer symbol with xNudge before showing enhancer reel.
                this.presentationTl.add(Mental.xNudgePresentation.replaceEnhancerSymbols());
                break;
            case winPresentationState.ENHANCER_OPEN:
                this.presentationTl.add(this.showEnhancerReel());
                break;
            case winPresentationState.ENHANCER_X_SPLIT:
                this.presentationTl.add(Mental.enhancerReelPresentation.playEnhancerXSplitPresentation());
                break;
            case winPresentationState.ENHANCER_X_NUDGE_REEL_2:
                // #92
                this.presentationTl.add(() => MentalFeatureIndicatorView.playXNudgeActiveIndicator());

                this.presentationTl.add(Mental.xNudgePresentation.playXNudgePresentation(0));
                break;
            case winPresentationState.ENHANCER_X_NUDGE_REEL_4:
                // #92
                this.presentationTl.add(() => MentalFeatureIndicatorView.playXNudgeActiveIndicator());

                this.presentationTl.add(Mental.xNudgePresentation.playXNudgePresentation(1));
                break;
            case winPresentationState.ENHANCER_X_NUDGE_WIN:
                this.presentationTl.add(Mental.xNudgePresentation.playNudgeWinPresentation());
                break;
            case winPresentationState.ENHANCER_X_WAYS_REEL_4:
                // #92
                if (this._gameData.specialReelsSymbols[0] !== ReelEnhancer.XWAYS) {
                    this.presentationTl.add(() => MentalFeatureIndicatorView.playXWaysActiveIndicator());
                }

                this.presentationTl.add(Mental.enhancerReelPresentation.expandXWaysPresentation());
                break;
        }
    }

    private scatterTwoXWaysCase(condition: string): void {
        switch (condition) {
            case winPresentationState.NORMAL_X_WAYS_REEL_2_0:
                this.presentationTl.add(this.playXWaysPresentation(1, 0));
                break;
            case winPresentationState.NORMAL_X_WAYS_REEL_2_1:
                this.presentationTl.add(this.playXWaysPresentation(1, 1));
                break;
            case winPresentationState.NORMAL_X_WAYS_REEL_4_0:
                this.presentationTl.add(this.playXWaysPresentation(3, 0));
                break;
            case winPresentationState.NORMAL_X_WAYS_REEL_4_1:
                this.presentationTl.add(this.playXWaysPresentation(3, 1));
                break;
            case winPresentationState.NORMAL_X_WAYS_REEL_4_2:
                this.presentationTl.add(this.playXWaysPresentation(3, 2));
                break;
            case winPresentationState.NORMAL_X_WAYS_REEL_4_3:
                this.presentationTl.add(this.playXWaysPresentation(3, 3));
                break;
            case winPresentationState.HIDE_ALL_X_WAYS:
                // #203
                this.presentationTl.add(() => SoundManager.playSoundEffect(MentalSoundConfig.X_WAYS), 0);

                // #92
                this.presentationTl.add(() => MentalFeatureIndicatorView.playXWaysActiveIndicator(), 0);

                this.presentationTl.add(Mental.s2xWaysFeature.hideAllXWaysOverlays(), 0);
                this.presentationTl.add(Mental.enhancerReelPresentation.hideAllXWaysOverlays(), 0);
                this.presentationTl.add(Mental.deadFeatureStickyMultiplier.playGiveOutAnimAfterXWaysReveal(this._gameData.stickyDeadSymCounter));
                break;
        }
    }

    private scatterTwoTransformCase(condition: string): void {
        switch (condition) {
            case winPresentationState.S2_SYM_TRANSFORM_2_0:
                this.presentationTl.add(this.playS2Transform(1, 0));
                this.presentationTl.add(Mental.deadFeatureStickyMultiplier.playGiveOutAnim(this._gameData.stickyDeadSymCounter, 1, this._gameData.scatter2Position[1], 0));
                break;
            case winPresentationState.S2_SYM_TRANSFORM_2_1:
                this.presentationTl.add(this.playS2Transform(1, 1));
                this.presentationTl.add(Mental.deadFeatureStickyMultiplier.playGiveOutAnim(this._gameData.stickyDeadSymCounter, 1, this._gameData.scatter2Position[1], 1));
                break;
            case winPresentationState.S2_SYM_TRANSFORM_4_0:
                this.presentationTl.add(this.playS2Transform(3, 0));
                this.presentationTl.add(Mental.deadFeatureStickyMultiplier.playGiveOutAnim(this._gameData.stickyDeadSymCounter, 3, this._gameData.scatter2Position[3], 0));
                break;
            case winPresentationState.S2_SYM_TRANSFORM_4_1:
                this.presentationTl.add(this.playS2Transform(3, 1));
                this.presentationTl.add(Mental.deadFeatureStickyMultiplier.playGiveOutAnim(this._gameData.stickyDeadSymCounter, 3, this._gameData.scatter2Position[3], 1));
                break;
            case winPresentationState.S2_SYM_TRANSFORM_4_2:
                this.presentationTl.add(this.playS2Transform(3, 2));
                this.presentationTl.add(Mental.deadFeatureStickyMultiplier.playGiveOutAnim(this._gameData.stickyDeadSymCounter, 3, this._gameData.scatter2Position[3], 2));

                break;
            case winPresentationState.S2_SYM_TRANSFORM_4_3:
                this.presentationTl.add(this.playS2Transform(3, 3));
                this.presentationTl.add(Mental.deadFeatureStickyMultiplier.playGiveOutAnim(this._gameData.stickyDeadSymCounter, 3, this._gameData.scatter2Position[3], 3));
                break;
        }
    }

    private scatterTwoXSplitCases(condition: string): void {
        let reelId: number = 0,
            innerSplitIdx: number = 0;
        switch (condition) {
            case winPresentationState.NORMAL_X_SPLIT_REEL_2_0:
                reelId = 1;
                innerSplitIdx = 0;
                this.presentationTl.add(this.playXSplitPresentation(1, 0, 1));
                break;
            case winPresentationState.NORMAL_X_SPLIT_REEL_2_1:
                reelId = 1;
                innerSplitIdx = 1;
                this.presentationTl.add(this.playXSplitPresentation(1, 1, this._gameData.firstS2FeatureTriggered.filter((val: string) => (val === ReelEnhancer.XSPLIT)).length));
                break;
            case winPresentationState.NORMAL_X_SPLIT_REEL_4_0:
                reelId = 3;
                innerSplitIdx = 0;
                this.presentationTl.add(this.playXSplitPresentation(3, 0, 1));
                break;
            case winPresentationState.NORMAL_X_SPLIT_REEL_4_1:
                reelId = 3;
                innerSplitIdx = 1;
                let xSplitCount: number = this._gameData.secondS2FeatureTriggered.filter((val: string) => (val === ReelEnhancer.XSPLIT)).length;
                // if both S2 are on same row
                if (this._gameData.scatter2Position[1] === this._gameData.scatter2Position[3]) {
                    // if first S2's 0 was also xSplit.
                    if (this._gameData.firstS2FeatureTriggered[0] === ReelEnhancer.XSPLIT) {
                        xSplitCount++;
                    }
                }

                this.presentationTl.add(this.playXSplitPresentation(3, 1, xSplitCount));
                break;
            case winPresentationState.NORMAL_X_SPLIT_REEL_4_2:
                reelId = 3;
                innerSplitIdx = 1;
                let xSplitCount2: number = this._gameData.secondS2FeatureTriggered.filter((val: string) => (val === ReelEnhancer.XSPLIT)).length;

                // if both S2 are on same row
                if (this._gameData.scatter2Position[2] === this._gameData.scatter2Position[3]) {
                    // if first S2's 0 was also xSplit.
                    if (this._gameData.firstS2FeatureTriggered[0] === ReelEnhancer.XSPLIT) {
                        xSplitCount2++;
                    }
                }
                this.presentationTl.add(this.playXSplitPresentation(3, 1, xSplitCount2));
                break;

            // impossible cases
            case winPresentationState.NORMAL_X_SPLIT_REEL_4_3:
                reelId = 3;
                innerSplitIdx = 1;
                let xSplitCount3: number = this._gameData.secondS2FeatureTriggered.filter((val: string) => (val === ReelEnhancer.XSPLIT)).length;

                // if both S2 are on same row
                if (this._gameData.scatter2Position[3] === this._gameData.scatter2Position[3]) {
                    // if first S2's 0 was also xSplit.
                    if (this._gameData.firstS2FeatureTriggered[0] === ReelEnhancer.XSPLIT) {
                        xSplitCount3++;
                    }
                }
                this.presentationTl.add(this.playXSplitPresentation(3, 1, xSplitCount3));
                break;
        }

        const time: number = this.presentationTl.totalDuration(),
            currentSymbolSize: number[][] = this._gameData.currentSymbolSize,
            normalReels: number[] = [0, 2, 4],
            numReels: number = 5;

        if (this._gameData.scatterPositions[1] === -1) {
            normalReels.splice(1, 0, 1)
        }
        if (this._gameData.scatterPositions[3] === -1) {
            normalReels.splice(normalReels.indexOf(4), 0, 3)
        }
        const splitIdx: number = reelId === 1 ? this._gameData.scatter2Position[1] : this._gameData.scatter2Position[3];
        for (let i: number = 0; i < numReels; i++) {
            let index: number = 0;
            if (reelId === 1 && i === 3 && this._gameData.scatter2Position[1] === this._gameData.scatter2Position[3]) {
                continue;
            }
            if (reelId === 1 && i === 1) {
                continue;
            }
            if (reelId === 3 && i === 3) {
                continue;
            }

            if (reelId === 3 && i === 1 && this._gameData.scatter2Position[1] === this._gameData.scatter2Position[3]) {
                index = innerSplitIdx;
                this.presentationTl.add(Mental.deadFeatureStickyMultiplier.playGiveOutAnimOnSpecialXSplit(this._gameData.stickyDeadSymCounter, i, splitIdx, index), time);
            } else {
                this.presentationTl.add(Mental.deadFeatureStickyMultiplier.playGiveOutAnim(this._gameData.stickyDeadSymCounter, i, splitIdx, currentSymbolSize[i][splitIdx]), time);
            }
        }
    }

    /**
     * resize reels during xways
     * @param reelId
     * @param isIncludeEnhancer
     * @param isWildRevealing
     * @private
     */
    private resizeForXWays(reelId: number, isIncludeEnhancer: boolean, isWildRevealing: boolean): void {
        const enhancerXWaysPlayedIndex: number = reelId == 1 ? 2 : 3;
        const S2XWaysPlayedIndex: number = reelId == 1 ? 0 : 1;
        if (this._gameData.xWaysPlayed[S2XWaysPlayedIndex]) {
            FeaturePresentationHelper.resizeSymbolWithInReel(reelId, (isIncludeEnhancer && this._gameData.xWaysPlayed[enhancerXWaysPlayedIndex]), isWildRevealing);
        }
    }

    /**
     * this will  be the last function to get called.
     * @private
     */
    private endPresentation(): void {
        // #195
        this._bigWinSoundName = "";

        // #230 => clear any Spider explosion
        this.presentationTl.add(() => MentalScatter2Explosion.clearAll(), 0);

        // #15, #227
        if (this._gameData.brokeTheBank) {
            this.presentationTl.to({}, {duration: 0.01});
            this.presentationTl.add(() => {
                Mental.winCap.playWinCapAnimations(() => this._controller.finish());
            });
        } else if (this._gameData.nextMode === MentalGameMode.NORMAL && this._gameData.mode.indexOf("FREESPIN") > -1) {
            const duration: number = this._gameData.betWayWins.length ? 0.5 : 0.01; // to fix #387
            this.presentationTl.to({}, {duration: duration});
            this.presentationTl.add(() => {
                this.showOutroOnFsEnd(this._controller);
            });
        } else {
            this.presentationTl.add(() => {
                this._controller.finish();
            }, "+=0.01");
        }
    }

    /**
     * function to play xSplit presentation
     */
    private playXSplitPresentation(xSplitReel: number, splitIdx: number, splitCount: number): TimelineLite {
        const tl: TimelineLite = new TimelineLite(),
            duration: number = (Mental.timeSegment * 3),
            baseSymIdForSplit: number = this._gameData.scatter2Position[xSplitReel];

        let explodeIdx: number = splitIdx,
            xWaysOccurred: boolean = false,
            retVal: IS2ExplodeIdxWithXWaysOccurred = this.getS2ExplodeIdx(xSplitReel, baseSymIdForSplit, explodeIdx, xWaysOccurred),
            knifePositions: IXSplitKnifePosWithXWays[] = [];

        tl.add(MentalScatter2Explosion.playScatter2ExplosionAnimation(xSplitReel, baseSymIdForSplit, retVal.explodeIdx, retVal.xWaysOccurred, 0, 0), 0);

        // #203
        tl.add(() => SoundManager.playSoundEffect(MentalSoundConfig.EXPLOSION_BLOOD), 0);

        tl.add(() => {knifePositions = this.getKnifeYPos(xSplitReel, splitIdx, splitCount)}, 0);

        // #92
        tl.add(() => MentalFeatureIndicatorView.playXSplitActiveIndicator(), 0.29);

        // play logo animation.
        tl.add(() => {
            const yPos: number = knifePositions[xSplitReel].yPos,
                symHeight: number = 198,
                currSymHeight: number = (symHeight / (this._gameData.currentSymbolSize[xSplitReel][baseSymIdForSplit])),
                perSymHeightXWays: number = FeaturePresentationHelper._symbolHeightOnReel[xSplitReel];

            Mental.xSplitAnimations.playXSplitLogoAnimationNormal(xSplitReel, (perSymHeightXWays > -1 ? perSymHeightXWays : currSymHeight), yPos);
        }, 0.29);
        // play knife animation on all 5 reels.
        tl.add(() => {
            MentalWinPresentation.playKnifeAnimation(knifePositions);

            // #116, #177, #203, #332
            SoundManager.playSoundEffect(MentalSoundConfig.X_SPLIT, 0, 1);
        }, Mental.timeSegment);

        // #86
        if (this._gameData.betWayWins.length > 0) {
            tl.add(() => MentalWinPresentation.playScatter2ExplodeInXSplitAction(baseSymIdForSplit, knifePositions), Mental.timeSegment * 2);

            tl.add(() => this.doSplitActionOnSymbols(xSplitReel, splitIdx), (Mental.timeSegment * 2) + 0.33);

            tl.add(() => {
            }, (duration));
        } else {
            tl.add(() => {
                this.doSplitActionOnSymbols(xSplitReel, splitIdx);

                // #332
                SoundManager.stopXSplit();
            }, (Mental.timeSegment * 2));
        }

        return tl;
    }

    private getKnifeYPos(xSplitReel: number, splitIdx: number, splitCount: number): IXSplitKnifePosWithXWays[] {
        const positions: IXSplitKnifePosWithXWays[] = [],
            numReels: number = 5,
            symHeight: number = 198,
            baseSymIdForSplit: number = this._gameData.scatter2Position[xSplitReel],
            currentSymbolSize: number[][] = this._gameData.currentSymbolSize; // shallow copy

        for (let i: number = 0; i < numReels; i++) {
            const ffOnCurrentItem: boolean = (this._gameData.fireFrameSymbolSize[i][baseSymIdForSplit] > 1),
                extraY: number = (i % 2 === 1 ? (symHeight / 2) : 0) + 90,
                currSymHeight: number = (symHeight / (currentSymbolSize[i][baseSymIdForSplit])),
                perSymHeightXWays: number = FeaturePresentationHelper._symbolHeightOnReel[i];
            let yPos: number = extraY,
                xWaysBgHeight: number = 0,
                xWaysOccurred: boolean = false,
                offset = (splitIdx === 0 ? 0.5 : (splitCount === 1 ? (ffOnCurrentItem ? 1.5 : 0.5) : (ffOnCurrentItem ? 2.5 : 1.5)));

            if (i === xSplitReel) {
                const retVal: IS2ExplodeIdxWithXWaysOccurred = this.getS2ExplodeIdx(i, baseSymIdForSplit, splitIdx, false),
                    actualIdx: number = retVal.explodeIdx,
                    symSegmentHeight: number = (symHeight / currentSymbolSize[i][baseSymIdForSplit]),
                    extraYAsPerActualIdx: number = (symSegmentHeight !== symHeight ? actualIdx * symSegmentHeight : 0);

                if (retVal.xWaysOccurred) {
                    yPos = extraY + (perSymHeightXWays * actualIdx) + (perSymHeightXWays / 2);

                    xWaysOccurred = true;
                } else {
                    yPos = extraY + ((symHeight * baseSymIdForSplit) + (symSegmentHeight / 2)) + extraYAsPerActualIdx;
                }
            } else if (i === 1 && this._gameData.xWaysPlayed[0]) {
                let actualIdx: number = splitIdx,
                    idxToCheckInFirstS2Feature: number = (this._gameData.firstS2FeatureTriggered.length === 1 ? 0 : splitIdx);

                xWaysOccurred = true;

                if (this._gameData.scatter2Position[1] === baseSymIdForSplit && this._gameData.firstS2FeatureTriggered[idxToCheckInFirstS2Feature] === ReelEnhancer.XWAYS) {
                    yPos = Mental.s2xWaysFeature.getXWaysAnimBgYPos(1, idxToCheckInFirstS2Feature, false);

                    xWaysBgHeight = Mental.s2xWaysFeature.getXWaysAnimBgHeight(1, idxToCheckInFirstS2Feature);
                } else {
                    if (actualIdx === 1) {
                        // at split idx 0, there wasn't xWays
                        if (currentSymbolSize[i][baseSymIdForSplit] === 3) {
                            actualIdx = 2;
                        }
                        // at split idx 0, there was xWays but split didn't run on that
                        else if (currentSymbolSize[i][baseSymIdForSplit] === 4) {
                            actualIdx = 3;
                        }
                        // at split idx 0, there was xWays and split also run on that xWays
                        else if (currentSymbolSize[i][baseSymIdForSplit] === 7) {
                            actualIdx = 6;
                        }
                        // if, there is no ff at the symbol which is going to be split
                        else if (currentSymbolSize[i][baseSymIdForSplit] === 1) {
                            actualIdx = 0;
                        }
                    }

                    // since the following explodeIdx is used as an absolute index if xWays had happened, so adding the previous symbol's sym size.
                    if (baseSymIdForSplit === 1) {
                        actualIdx += currentSymbolSize[i][0];
                    }

                    yPos = extraY + (perSymHeightXWays * actualIdx) + (perSymHeightXWays / 2);
                }
            } else {
                yPos += (symHeight * baseSymIdForSplit) + (currSymHeight * offset);
            }

            positions.push({
                yPos: yPos,
                xWaysOccurred: xWaysOccurred,
                xWaysBgHeight: xWaysBgHeight
            });
        }

        return positions;
    }

    private doSplitActionOnSymbols(xSplitReel: number, splitIdx: number): void {
        const numReels: number = 5,
            baseSymIdForSplit: number = this._gameData.scatter2Position[xSplitReel],
            currentSymbolSize: number[][] = this._gameData.currentSymbolSize; // shallow copy

        Mental.xSplitAnimations.stopAllAnimationsNormal();

        for (let i: number = 0; i < numReels; i++) {
            let isSplitActionOnXWays: boolean = false;

            const sym: ReelModelSymbolData = Mental.reelArea.getReel(i).model.getReelSymbolAt(baseSymIdForSplit),
                symNames: string[] = ((sym.name === "S2") ? (this.getSymNameForXSplitReel(xSplitReel, splitIdx, i)) : this.getSplitSymbol(xSplitReel, splitIdx, i, sym.name));

            // only reelId ===1 can pass this check, no other reel id should pass this.
            if ((i === 1) && (this._gameData.scatter2Position[i] > -1) && (i !== xSplitReel) && (this._gameData.scatter2Position[i] === baseSymIdForSplit)) {
                isSplitActionOnXWays = this.checkForXWaysOnXSplit(splitIdx);
            }

            currentSymbolSize[i][baseSymIdForSplit] += (isSplitActionOnXWays ? (xSplitReel === 1 ? 1 : 3) : 1); // don't cache this due to scope in timeline.

            if (i === 1 && xSplitReel === 3 && this._gameData.firstS2FeatureTriggered.length > 1 && this._gameData.scatter2Position[1] === baseSymIdForSplit) {
                let xSplitCount: number = 1;
                for (let j: number = 0; j < splitIdx; j++) {
                    if (this._gameData.secondS2FeatureTriggered[j] === ReelEnhancer.XSPLIT) {
                        xSplitCount++;
                    }
                }
                const scatter2position: number = baseSymIdForSplit; // automatically same, if we see it's if block's condition
                if (isSplitActionOnXWays) {
                    for (let j = 0; j < 3; j++) {
                        this._gameData.deadMultiplierPlacedPositions[i][scatter2position].splice(splitIdx + xSplitCount, 0, false);
                        this._gameData.deadMultiplierPlacedValue[i][scatter2position].splice(splitIdx + xSplitCount, 0, 1);

                        this._gameData.deadMultiplierPlacedValue[i][scatter2position].pop();
                        this._gameData.deadMultiplierPlacedPositions[i][scatter2position].pop();
                    }
                } else {
                    this._gameData.deadMultiplierPlacedPositions[i][scatter2position].splice(splitIdx + xSplitCount, 0, false);
                    this._gameData.deadMultiplierPlacedValue[i][scatter2position].splice(splitIdx + xSplitCount, 0, 1);

                    this._gameData.deadMultiplierPlacedValue[i][scatter2position].pop();
                    this._gameData.deadMultiplierPlacedPositions[i][scatter2position].pop();
                }
            } else {
                // #305
                EnhancerReelPresentation.handleDeadMultiData(this._gameData, i, xSplitReel, baseSymIdForSplit, splitIdx);
            }

            XFeaturePresentation.playXWaysFeaturePresentation(i, baseSymIdForSplit, symNames, currentSymbolSize[i][baseSymIdForSplit], true, (i !== xSplitReel));

            // if applicable, resize reel 1 and 3 considering if xWays is part of enhancer.
            if (i === 1) {
                let isXWaysInEnhancer: boolean = this._gameData.xWaysPlayed[2]; // 2 is index of 1st enhancer

                if (this._gameData.xWaysPlayed[0] || isXWaysInEnhancer) {
                    FeaturePresentationHelper.resizeSymbolWithInReel(1, isXWaysInEnhancer);

                    if (isSplitActionOnXWays) {
                        // to fix 3-xWays case on 2nd reel
                        const index: number = (this._gameData.firstS2FeatureTriggered.length > 1) ? splitIdx : 0;
                        Mental.s2xWaysFeature.resizeXWaysOverlayFromXSplit(this._gameData.firstS2FeatureTriggered.length, index, i, new TimelineLite());
                    } else {
                        Mental.s2xWaysFeature.resizeXWaysOverlay(this._gameData.firstS2FeatureTriggered.length, i, new TimelineLite());
                    }
                }
            } else if (i === 3) {
                let isXWaysInEnhancer: boolean = this._gameData.xWaysPlayed[3]; // 3 is index of 2nd enhancer

                if (this._gameData.xWaysPlayed[1] || isXWaysInEnhancer) {
                    FeaturePresentationHelper.resizeSymbolWithInReel(3, isXWaysInEnhancer);

                    Mental.s2xWaysFeature.resizeXWaysOverlay(this._gameData.secondS2FeatureTriggered.length, i, new TimelineLite());
                }
            }

            // #116
            SoundManager.playSoundEffect(MentalSoundConfig.REEL_SPLIT[i]);
        }
    }

    /**
     * added this function to fix #189
     * when symbol revealed from S2 and get splitted then updating firstS2SymbolName.
     * @param xSplitReel
     * @param splitIdx
     * @param reelId
     * @param symbolName
     * @private
     */
    private getSplitSymbol(xSplitReel: number, splitIdx: number, reelId: number, symbolName: string): string[] {
        const symName: string[] = [symbolName];
        if (this._gameData.firstS2FeatureTriggered.length == 1 && reelId !== xSplitReel && reelId === 1 && this._gameData.scatter2Position[1] === this._gameData.scatter2Position[3]
            && !this._gameData.firstS2FeatureTriggered.includes(ReelEnhancer.XWAYS) && !this._gameData.firstS2FeatureTriggered.includes(ReelEnhancer.XSPLIT)) {
            if (splitIdx === 0) {
                this._gameData.firstS2SymbolName.unshift(this._gameData.firstS2SymbolName[0]);
            } else {
                this._gameData.firstS2SymbolName.push(this._gameData.firstS2SymbolName[this._gameData.firstS2SymbolName.length - 1]);
            }
        }

        return symName;
    }

    private checkForXWaysOnXSplit(splitIdx: number): boolean {
        const firstS2Feature: string[] = this._gameData.firstS2FeatureTriggered;
        let isSplitActionOnXWays: boolean;

        if (splitIdx === 0) {
            isSplitActionOnXWays = (firstS2Feature[0] === ReelEnhancer.XWAYS);
        } else {
            let idx: number;
            if (firstS2Feature.length > 1) {
                idx = 1;
            } else {
                idx = 0;
            }
            isSplitActionOnXWays = (firstS2Feature[idx] === ReelEnhancer.XWAYS);
        }

        return isSplitActionOnXWays;
    }

    private getSymNameForXSplitReel(xSplitReel: number, splitIdx: number, reelId: number): string[] {
        let retVal: string[];

        if (reelId !== xSplitReel && this._gameData.scatter2Position[1] === this._gameData.scatter2Position[3]) {
            if (reelId === 1) {
                retVal = this._gameData.firstS2SymbolName;
                if (splitIdx === 0) {
                    const length: number = (this._gameData.firstS2FeatureTriggered[0] === ReelEnhancer.XWAYS) ? 3 : 1;
                    for (let i: number = 0; i < length; i++) {
                        retVal.unshift(retVal[0]);
                    }
                } else {
                    let idx: number;
                    if (this._gameData.firstS2FeatureTriggered.length > 1) {
                        idx = 1;
                    } else {
                        idx = 0;
                    }
                    const length: number = (this._gameData.firstS2FeatureTriggered[idx] === ReelEnhancer.XWAYS) ? 3 : 1;
                    for (let i: number = 0; i < length; i++) {
                        retVal.push(retVal[retVal.length - 1]);
                    }
                }
            } else {
                retVal = this._gameData.secondS2SymbolName;
                if (splitIdx === 0) {
                    retVal.unshift("S2");
                } else {
                    retVal.push("S2");
                }
            }
        } else {
            retVal = (xSplitReel === 1 ? this._gameData.firstS2SymbolName : this._gameData.secondS2SymbolName);

            if (splitIdx === 0) {
                retVal[0] = "W";
                retVal.unshift("W");
            } else {
                const s2Idx: number = retVal.indexOf("S2");
                retVal.splice(s2Idx, 1, "W", "W");
            }
        }

        // retVal is a shallow copy, so it will effect back the original names, and we want it like this so it can be used in features played after this.
        return retVal;
    }

    /**
     * function to split symbols when x ways is present
     */
    private playXWaysPresentation(reelId: number, index: number): TimelineMax {
        const tl: TimelineMax = new TimelineMax();

        let explodeIdx: number = index,
            xWaysOccurred: boolean = false,
            retVal: IS2ExplodeIdxWithXWaysOccurred;

        for (let j: number = 0; j < 2; j++) { // 2 positions available for S2 top and bottom
            const symbolName = this._gameData.mysterySymbols[reelId][j];
            if (symbolName !== "X") {
                retVal = this.getS2ExplodeIdx(reelId, j, explodeIdx, xWaysOccurred);
                explodeIdx = retVal.explodeIdx;
                xWaysOccurred = retVal.xWaysOccurred;

                Mental.xWaysCounter.updateXwaysCounterFromS2Xways(reelId);

                // #195
                tl.add(() => SoundManager.playSoundEffect(MentalSoundConfig.EXPLOSION_ELECTRICITY), 0);

                tl.add(MentalScatter2Explosion.playScatter2ExplosionAnimation(reelId, j, explodeIdx, xWaysOccurred, 0, 0), 0);

                // #92
                tl.add(() => MentalFeatureIndicatorView.playXWaysActiveIndicator(), 0.29);

                tl.add(Mental.s2xWaysFeature.showXWaysLogoBg(reelId, j, index), 0.29);
                tl.add(Mental.s2xWaysFeature.expandXWaysPresentation(reelId, j, index), Mental.timeSegment);
            }
        }

        return tl;
    }

    /**
     * function to show sticky symbol in freeSpins
     */
    private stickyScatter(): void {
        const stickyPos: number[] = this._gameData.stickyScatter2;
        for (let i = 0; i < stickyPos.length; i++) {
            Mental.stickyScatter.stickScatterOnPos(i, stickyPos[i]);
        }
    }

    /**
     * function to show outro on end of presentation
     * @param flowStateController
     */
    private showOutroOnFsEnd(flowStateController: FlowStateController): void {
        Mental.fsOutro.showOutro(() => {
            flowStateController.finish();
        });
    }

    /**
     * adding win field to timeline here.
     */
    private showWinField(): void {
        Mental.keypadPlugin.setWin(this._gameData.accumulatedRoundWin, this.useTotalWin, MentalHelper.isWinBelowStake());
    }

    private skip(): void {
        if (this._bigWinSoundName) {
            SoundManager.fadeSound(this._bigWinSoundName, 1, 0, 0.1);

            this._bigWinSoundName = "";
        }

        // #246
        if (this._winSequenceArray[0] === winPresentationState.DEAD_FEATURE) {
            Mental.deadFeaturePresentation.removeAllVisibleDeadToActiveAnimations();
        }

        this.presentationTl.totalProgress(1);

        // #332
        SoundManager.stopXSplit();

        // #365
        if (!this._winSequenceArray.length && Mental.bigWinPresentation.isBigWinTriggered()) {
            Mental.bigWinPresentation.onAbort();
        }
    }

    private playAllWinSymbols(): TimelineMax {
        const tl: TimelineMax = new TimelineMax(),
            alreadyWinBoxPresent: boolean[][][] = this.resetWinBoxPresentData();

        tl.add(() => {
            const winGroups: IBetWayWinData[] = this._gameData.betWayWins,
                animName: string = (this._gameData.currentWinRatioConfig.duration > 0.001 ? "frameWithFlash" : "frame");
            let winFrameCount: number = 0;

            for (let groups: number = 0; groups < winGroups.length; groups++) {
                const winGroup: IBetWayWinData = winGroups[groups];

                for (let i: number = 0; i < winGroup.reelWinPositions.length; i++) {  // reels
                    for (let j: number = 0; j < winGroup.reelWinPositions[i].length; j++) { //reel
                        const symbol: boolean[] = winGroup.reelWinPositions[i][j];
                        let resizedReel = false;

                        if (i === 1 || i === 3) {
                            const s2Index: number = (i === 1) ? 0 : 1;
                            const enhancerIndex: number = (i === 1) ? 2 : 3;
                            resizedReel = (this._gameData.xWaysPlayed[s2Index] || this._gameData.xWaysPlayed[enhancerIndex]);
                        }

                        for (let k: number = 0; k < symbol.length; k++) { // symbol
                            if (symbol[k] && !alreadyWinBoxPresent[i][j][k]) { // 1 position should  have  1 winBox playing
                                alreadyWinBoxPresent[i][j][k] = true;
                                if ((i === 1 || i === 3) && j === 2) { //  there could be xWays on reel, but not on enhancer.
                                    const enhancerIndex: number = (i === 1) ? 2 : 3;
                                    resizedReel = (this._gameData.xWaysPlayed[enhancerIndex]);
                                }

                                const heightPerVisibleSymAfterResize: number = resizedReel ? FeaturePresentationHelper._symbolHeightOnReel[i] : 198 / symbol.length;
                                let isEnhancePos = ((i === 1 || i === 3) && (j === 2));
                                let isSingleSymInEnhancer: boolean = isEnhancePos && symbol.length === 1;

                                Mental.winHighlight.playInitialWinAnimation({
                                    reelId: i,
                                    symbolId: j,
                                    innerSymbolId: k,
                                    animName: animName,
                                    resizedReel: resizedReel,
                                    singleSymbolInEnhancer: isSingleSymInEnhancer,
                                    symbolHeight: heightPerVisibleSymAfterResize,
                                    winFrameIndex: winFrameCount++,
                                    timeScale: 1
                                });
                            }
                        }
                    }
                }
            }
        }, 0);

        // let the above complete
        tl.to({}, {duration: 0.8});
        return tl;
    }

    // #252
    private playAllWinSymbolsBigWinInit(): TimelineMax {
        const tl: TimelineMax = new TimelineMax();

        tl.add(() => {
            const alreadyWinBoxPresent: boolean[][][] = this.resetWinBoxPresentData(),
                winGroups: IBetWayWinData[] = this._gameData.betWayWins,
                animName: string = "intenseFrame";
            let winFrameCount: number = 0;

            for (let groups: number = 0; groups < winGroups.length; groups++) {
                const winGroup: IBetWayWinData = winGroups[groups];

                for (let i: number = 0; i < winGroup.reelWinPositions.length; i++) {  // reels
                    for (let j: number = 0; j < winGroup.reelWinPositions[i].length; j++) { //reel
                        const symbol: boolean[] = winGroup.reelWinPositions[i][j];
                        let resizedReel = false;

                        if (i === 1 || i === 3) {
                            const s2Index: number = (i === 1) ? 0 : 1;
                            const enhancerIndex: number = (i === 1) ? 2 : 3;
                            resizedReel = (this._gameData.xWaysPlayed[s2Index] || this._gameData.xWaysPlayed[enhancerIndex]);
                        }

                        for (let k: number = 0; k < symbol.length; k++) { // symbol
                            if (symbol[k] && !alreadyWinBoxPresent[i][j][k]) { // 1 position should  have  1 winBox playing
                                alreadyWinBoxPresent[i][j][k] = true;
                                if ((i === 1 || i === 3) && j === 2) { //  there could be xWays on reel, but not on enhancer.
                                    const enhancerIndex: number = (i === 1) ? 2 : 3;
                                    resizedReel = (this._gameData.xWaysPlayed[enhancerIndex]);
                                }

                                const heightPerVisibleSymAfterResize: number = resizedReel ? FeaturePresentationHelper._symbolHeightOnReel[i] : 198 / symbol.length;
                                let isEnhancePos = ((i === 1 || i === 3) && (j === 2));
                                let isSingleSymInEnhancer: boolean = isEnhancePos && symbol.length === 1;

                                Mental.winHighlight.playToggleAnimation({
                                    reelId: i,
                                    symbolId: j,
                                    innerSymbolId: k,
                                    animName: animName,
                                    resizedReel: resizedReel,
                                    singleSymbolInEnhancer: isSingleSymInEnhancer,
                                    symbolHeight: heightPerVisibleSymAfterResize,
                                    winFrameIndex: winFrameCount++,
                                    timeScale: (1 / 0.9) // #348
                                });
                            }
                        }
                    }
                }
            }
        }, 0);

        // let the above stay
        tl.to({}, {duration: 0.7}); // #348
        return tl;
    }

    private resetWinBoxPresentData(): boolean[][][] {
        const numberOfSymbolPerReel: number = 3;
        const totalNumOfReels: number = 5;
        const winFrame: boolean[][][] = [];
        for (let i: number = 0; i < totalNumOfReels; i++) {
            winFrame[i] = [];
            for (let j: number = 0; j < numberOfSymbolPerReel; j++) {
                winFrame[i][j] = [];
                for (let k: number = 0; k < this._gameData.currentSymbolSize[i][j]; k++) {
                    winFrame[i][j].push(false);
                }
            }
        }
        return winFrame;
    }

    private playS2Transform(reelId: number, transformIdx: number): TimelineLite {
        const tl: TimelineLite = new TimelineLite(),
            possibleSymbols: string[] = ["M1", "M2", "M3", "M4", "M5", "W", "D"],
            symId: number = this._gameData.scatter2Position[reelId],
            reel: MentalReel = Mental.reelArea.getReel(reelId),
            symbolData: ReelModelSymbolData = reel.model.getReelSymbolAt(symId),
            symbol: MentalNormalSymbol = <MentalNormalSymbol>symbolData.positionable,
            allFeatureTriggered: string[] = reelId === 1 ? this._gameData.firstS2FeatureTriggered : this._gameData.secondS2FeatureTriggered;

        let newSymbolParts: string[] = (reelId === 1) ? this._gameData.firstS2SymbolName : this._gameData.secondS2SymbolName,
            explodeIdx: number = transformIdx,
            xWaysOccurred: boolean = false,
            retVal: IS2ExplodeIdxWithXWaysOccurred = this.getS2ExplodeIdx(reelId, symId, explodeIdx, xWaysOccurred);

        // #322(#278) => since, S2 haven't transformed into anything else
        if (this._winSequenceArray[0] !== winPresentationState.ALL_S2_AT_ONCE_FEATURE_BUY) {
            explodeIdx = retVal.explodeIdx;
            xWaysOccurred = retVal.xWaysOccurred;
        }

        // #195
        tl.add(() => SoundManager.playSoundEffect(MentalSoundConfig.EXPLOSION[(MentalHelper.getRandomIntInRange(0, 2))]), 0);

        tl.add(MentalScatter2Explosion.playScatter2ExplosionAnimation(reelId, symId, explodeIdx, xWaysOccurred, 0, 0));

        tl.add(() => {
            let index: number = transformIdx;
            if (possibleSymbols.includes(allFeatureTriggered[transformIdx])) {
                if (reelId === 3) {
                    index = newSymbolParts.indexOf("S2");
                    newSymbolParts[index] = allFeatureTriggered[transformIdx];
                } else {
                    // below checks to fix dead symbol in middle of xways
                    if (index == 0) {
                        newSymbolParts[0] = allFeatureTriggered[transformIdx];
                    } else if (index == 1) {
                        newSymbolParts[newSymbolParts.length - 1] = allFeatureTriggered[transformIdx]
                    }
                }
            }

            if (allFeatureTriggered[transformIdx] === "D") {
                Mental.deadFeaturePresentation.tryDeadToGoldOnFeatureReel(reelId, symId);
                if (Mental.deadFeaturePresentation.getDeadCount() > 1) {
                    //#189
                    //newSymbolParts[index] = "DG";
                    for (let i: number = 0; i < newSymbolParts.length; i++) {
                        if (newSymbolParts[i] === "D") {
                            newSymbolParts[i] = "DG";
                        }
                    }
                }
            }
        }, 0.29);

        tl.add(() => {
            if (allFeatureTriggered.length === 1) {
                const newSym: MentalNormalSymbol = <MentalNormalSymbol>reel.model.updateReelSymbolAt(symId, allFeatureTriggered[0]).positionable;
                reel.layerSet.layers[0].addChildAt(newSym._symbolLayer, symId);
                newSym._symbolLayer.position = symbol._symbolLayer.position;
                reel.removeSymbol(symbolData, ReelModelState.STOPPED);

                // #281
                if (newSymbolParts[0] === "W") {
                    newSym.setState(MentalSymbolStates.LANDING);
                }
            } else {
                const newSym: MentalNormalSymbol = <MentalNormalSymbol>reel.model.getStopSymbolAt(symId).positionable;
                const multi: number[] = Mental.model.gameData.deadMultiplierPlacedValue[reelId][symId];

                newSym.splitInMultiSymbols(newSymbolParts.length, newSymbolParts, multi);
                newSym.allowedToResizeSegments = true;
                newSym.setStateAsPerParts(newSymbolParts.length, allFeatureTriggered[transformIdx] !== "W");
            }
            if (reelId === 1) {
                this._gameData.firstS2SymbolName = newSymbolParts;
            } else {
                this._gameData.secondS2SymbolName = newSymbolParts;
            }
        }, 0.29);

        /**
         * commenting this code to fix #287,
         * also,tryDeadToGoldOnFeatureReel is handled above at same time
         * not sure why added this code here,
         * if this code seems mandatory to add send newSymbolParts array with it.
         */
        // if (allFeatureTriggered[transformIdx] === "D" && Mental.deadFeaturePresentation.getDeadCount() > 1) {
        //     tl.add(Mental.deadFeaturePresentation.tryDeadToGoldOnFeatureReel(reelId, symId), 0.29);
        // }

        tl.add(() => this.resizeForXWays(reelId, true, (allFeatureTriggered[transformIdx] === "W")), 0.29);

        return tl;
    }

    // handling cases, when S2 is not at 0(visually) and there might be case of xWays being played on that reel already.
    private getS2ExplodeIdx(s2Reel: number, s2SymId: number, explodeIdx: number, xWaysOccurred: boolean): IS2ExplodeIdxWithXWaysOccurred {
        const newSymbolParts: string[] = (s2Reel === 1) ? this._gameData.firstS2SymbolName : this._gameData.secondS2SymbolName;

        if (explodeIdx !== 0) {
            explodeIdx = newSymbolParts.indexOf("S2");

            if (s2Reel === 1 && this._gameData.xWaysPlayed[0]) {
                xWaysOccurred = true;

                // since the following explodeIdx is used as an absolute index if xWays had happened, so adding the previous symbol's sym size.
                if (s2SymId === 1) {
                    explodeIdx += this._gameData.currentSymbolSize[s2Reel][0];
                }
            } else if (s2Reel === 3 && this._gameData.xWaysPlayed[1]) {
                xWaysOccurred = true;

                // since the following explodeIdx is used as an absolute index if xWays had happened, so adding the previous symbol's sym size.
                if (s2SymId === 1) {
                    explodeIdx += this._gameData.currentSymbolSize[s2Reel][0];
                }
            }
        }

        return {
            explodeIdx: explodeIdx,
            xWaysOccurred: xWaysOccurred
        }
    }

    private playBigWinLevelSound(name: string): void {
        this._bigWinSoundName = name;
        SoundManager.playSoundEffect(this._bigWinSoundName, 0, 1);
    }

    // #278
    private handleAllS2AtOnceFeatureBuy(): TimelineLite {
        const tl: TimelineLite = new TimelineLite(),
            firstS2Feature: string[] = this._gameData.firstS2FeatureTriggered,
            secondS2Feature: string[] = this._gameData.secondS2FeatureTriggered;

        // reel id 1's S2
        for (let i: number = 0; i < firstS2Feature.length; i++) {
            if (firstS2Feature[i]) {
                tl.add(this.playS2Transform(1, i), 0);
            }
        }

        // reel id 3's S2
        for (let i: number = 0; i < secondS2Feature.length; i++) {
            if (secondS2Feature[i]) {
                tl.add(this.playS2Transform(3, i), 0);
            }
        }

        return tl;
    }
}